import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useUserCheck } from '../hooks/api/useUserCheck'
import { usePermissions } from '../hooks/usePermissions'
import { useCreateDevice } from '../hooks/api/useDevices'
import { useDevicesLive } from '../hooks/api/useDevicesLive'
import { useCompanyById } from '../hooks/api/useCompanies'
import { DashboardLayout } from '../components/layout/DashboardLayout'
import { AddDeviceModal } from '../components/modals/AddDeviceModal'
import { DeviceHeader } from '../components/devices/DeviceHeader'
import { DeviceSearchFilter } from '../components/devices/DeviceSearchFilter'
import { DeviceTable } from '../components/devices/DeviceTable'
import { Pagination } from '../components/ui/Pagination'
import { DeviceFilters } from '../types/device-filters'

export const DevicesPage = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { data: userRecord, isLoading, error } = useUserCheck(user?.id || '')
  const { hasPermission } = usePermissions({ userRole: userRecord?.role || null })
  const [searchQuery, setSearchQuery] = useState('')
  const [showAddDeviceModal, setShowAddDeviceModal] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [filters, setFilters] = useState<DeviceFilters>({
    status: '',
    organization: '',
    deviceType: '',
    hasTickets: ''
  })
  const itemsPerPage = 10

  // Get company info to get kaseya_group_id
  const { data: company } = useCompanyById(userRecord?.company_id)

  // Get devices from Kaseya cache and filter by company's group
  const { data: allDevices = [], isLoading: devicesLoading } = useDevicesLive()
  const createDeviceMutation = useCreateDevice()

  // Filter devices by company's kaseya_group_id
  const devices = company?.kaseya_group_id
    ? allDevices.filter(d => d.group_id === company.kaseya_group_id)
    : []

  const handleAddDevice = () => {
    setShowAddDeviceModal(true)
  }

  const handleCloseModal = () => {
    setShowAddDeviceModal(false)
  }

  const handleSubmitDevice = async (deviceData: any) => {
    const targetCompanyId = deviceData.companyId || userRecord?.company_id

    if (!targetCompanyId) {
      console.error('No company ID available')
      return
    }

    try {
      await createDeviceMutation.mutateAsync({
        deviceData: {
          name: deviceData.deviceName,
          os_type: deviceData.operatingSystem,
          device_type: deviceData.deviceType,
          mac_address: deviceData.macAddress,
          user_id: deviceData.deviceOwner || undefined,
          kaseya_group_id: deviceData.kaseyaGroupId,
          deployment_email: deviceData.deploymentEmail || undefined,
          deployment_os: deviceData.operatingSystem,
          deployment_arch: deviceData.architecture
        },
        companyId: targetCompanyId,
        sendAgentNow: deviceData.sendAgentNow
      })
      setShowAddDeviceModal(false)
    } catch (error) {
      console.error('Error creating device:', error)
    }
  }

  const handleSearchChange = (query: string) => {
    setSearchQuery(query)
    setCurrentPage(1)
  }

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }))
    setCurrentPage(1)
  }

  const handleClearFilters = () => {
    setFilters({
      status: '',
      organization: '',
      deviceType: '',
      hasTickets: ''
    })
    setCurrentPage(1)
  }

  const handleDeviceClick = (deviceId: string) => {
    navigate(`/devices/${deviceId}`)
  }

  // Format devices for display and filter based on search query and filters
  // Map DeviceLive to the format expected by DeviceTable
  const displayDevices = devices.map(device => ({
    id: device.kaseya_device_id,
    deviceId: device.kaseya_device_id?.slice(0, 8) || device.id.slice(0, 8),
    name: device.name,
    organization: device.company_name || device.organization_name || 'Unknown',
    status: device.status === 'unknown' ? 'offline' : device.status,
    osType: device.group_name || 'Unknown',
    deviceType: device.group_name || 'Unknown',
    email: '-',
    lastActive: device.last_synced_at
      ? new Date(device.last_synced_at).toLocaleDateString()
      : '-',
    tickets: device.ticket_count || 0
  }))
  
  const filteredDevices = displayDevices.filter(device => {
    // Search filter
    const matchesSearch = 
      device.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      device.deviceId.toLowerCase().includes(searchQuery.toLowerCase()) ||
      device.organization.toLowerCase().includes(searchQuery.toLowerCase())
    
    // Status filter
    const matchesStatus = !filters.status || device.status === filters.status
    
    // Organization filter
    const matchesOrganization = !filters.organization || device.organization === filters.organization
    
    // Device type filter (based on OS type)
    const matchesDeviceType = !filters.deviceType || device.osType === filters.deviceType
    
    // Tickets filter
    const matchesTickets = !filters.hasTickets || 
      (filters.hasTickets === 'with-tickets' && device.tickets > 0) ||
      (filters.hasTickets === 'without-tickets' && device.tickets === 0)
    
    return matchesSearch && matchesStatus && matchesOrganization && matchesDeviceType && matchesTickets
  })

  // Pagination logic
  const totalPages = Math.ceil(filteredDevices.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const paginatedDevices = filteredDevices.slice(startIndex, endIndex)

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  if (isLoading || devicesLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-3 border-gray-400 border-t-transparent border-r-transparent mx-auto mb-4"></div>
          <p className="text-gray-600">Loading devices...</p>
        </div>
      </div>
    )
  }

  if (error || !userRecord) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <p className="text-red-600 text-sm font-medium mb-2">❌ User not found in database!</p>
            <p className="text-red-500 text-xs mb-2">This account was created before user record creation was implemented.</p>
            <p className="text-red-500 text-xs">Please create a new account to test the full flow.</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <DashboardLayout 
      userRole={userRecord.role} 
      adminLevel={userRecord.admin_level}
      userName={userRecord.full_name || user?.email}
      userEmail={user?.email}
    >
      <div className="space-y-6">
        {/* Header */}
        <DeviceHeader 
          onAddDevice={handleAddDevice}
          canCreateDevices={hasPermission('CREATE_DEVICES')}
        />

        {/* Search and Filter Controls */}
        <DeviceSearchFilter
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
          filters={filters}
          onFilterChange={handleFilterChange}
          onClearFilters={handleClearFilters}
        />

        {/* Devices Table */}
        <DeviceTable
          devices={paginatedDevices}
          onDeviceClick={handleDeviceClick}
          searchQuery={searchQuery}
        />

        {/* Pagination */}
        {filteredDevices.length > itemsPerPage && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={filteredDevices.length}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
          />
        )}
      </div>

      {/* Add Device Modal */}
      <AddDeviceModal
        isOpen={showAddDeviceModal}
        onClose={handleCloseModal}
        onAddDevice={handleSubmitDevice}
      />

    </DashboardLayout>
  )
}
